import {
  Reference,
  ReferenceInputProps
} from '@healthnz-prm/fhir-engine-renderer/types'
import { useParams } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { xApiKey, apiEndpointUrl } from '@/env'
import { handleNetworkResponse, NotFoundError } from '@/lib/error'
import { useAuth } from '@/contexts/auth/auth'
import { Bundle, RenderResourceProps } from './types'
import SearchInput from './searchInput'
import { TypedReferenceInput } from './TypedReferenceInput'
import { SearchMethodToggleProps } from './SearchMethodToggle'
import { HugeiconsIcon } from '@hugeicons/react'
import { Building06Icon } from '@hugeicons/core-free-icons'

interface Location {
  resourceType: 'Location'
  id: string
  name: string
  alias?: string[]
}

interface OperationOutcome {
  resourceType: 'OperationOutcome'
}

async function fromReference(
  authToken: string,
  questionnaireId: string | undefined,
  reference: Reference
): Promise<Location | undefined> {
  const locationId = reference.reference?.split('/')[1]

  if (!locationId) return undefined

  return fetch(
    `${apiEndpointUrl}/v1/hip/${questionnaireId}/location/${locationId}`,
    {
      method: 'get',
      headers: {
        Authorization: authToken,
        'x-api-key': xApiKey
      }
    }
  )
    .then(handleNetworkResponse)
    .then(res => res?.json() as Promise<Location | OperationOutcome>)
    .then(res => {
      if (res.resourceType === 'OperationOutcome') throw new NotFoundError()
      return res
    })
    .catch(e => {
      if (e instanceof NotFoundError)
        throw new Error('Could not find the selected location.')

      throw new Error(
        'An error occurred while loading the location. Please try again.'
      )
    })
}

async function searchLocation(
  authToken: string,
  questionnaireId: string | undefined,
  name: string
): Promise<Location[]> {
  return fetch(
    `${apiEndpointUrl}/v1/hip/${questionnaireId}/location/search?name=${name}`,
    {
      method: 'get',
      headers: {
        Authorization: authToken,
        'x-api-key': xApiKey
      }
    }
  )
    .then(handleNetworkResponse)
    .then(res => res?.json())
    .then((bundle: Bundle<Location> | undefined) => {
      return bundle?.entry?.map(entry => entry.resource) ?? []
    })
    .catch(() => {
      throw new Error('An error occurred while searching. Please try again.')
    })
}

function RenderResource({ resource }: RenderResourceProps<Location>) {
  return (
    <div className='w-full grow py-1'>
      <p className='font-medium'>
        {resource.name ?? (
          <span className='text-destructive'>No recorded name</span>
        )}
      </p>
      <p className='text-muted-foreground text-xs'>ID: {resource.id ?? '-'}</p>
      {resource.alias?.length ? (
        <p className='text-muted-foreground text-xs'>
          Other names: {resource.alias.join(', ')}
        </p>
      ) : null}
    </div>
  )
}

function toReference(resource: Location): Reference {
  return {
    display: resource.name,
    reference: `Location/${resource.id}`
  }
}

function makeInputs(
  authToken: string,
  questionnaireId: string | undefined
): SearchMethodToggleProps<Location>['inputs'] {
  return [
    {
      label: 'LocationName',
      component: props => (
        <SearchInput
          {...props}
          icon={
            <HugeiconsIcon
              icon={Building06Icon}
              strokeWidth={2}
              className='size-4 text-primary'
            />
          }
          placeholder='Search for facility by name'
          onSearch={async name =>
            searchLocation(authToken, questionnaireId, name)
          }
          resourceToLabel={resource => resource.name ?? '-'}
        />
      )
    }
  ]
}

export function LocationReferenceInput(props: ReferenceInputProps) {
  const authToken = useAuth()
  const { questionnaireId } = useParams()

  const inputs = useMemo(
    () => makeInputs(authToken, questionnaireId),
    [authToken, questionnaireId]
  )

  const loadReference = useCallback(
    (reference: Reference) =>
      fromReference(authToken, questionnaireId, reference),
    [authToken, questionnaireId]
  )

  return (
    <TypedReferenceInput
      {...props}
      RenderResource={RenderResource}
      fromReference={loadReference}
      toReference={toReference}
      inputs={inputs}
    />
  )
}
